import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import cn from "classnames";
import styles from "./SessionsFormationDetail.module.sass";
import { Link } from "react-router-dom";
import Icon from "../../components/Icon";

// Screens
import NameAndDescription from "./NameAndDescription";
import SignaturesAndAbsences from "./SignaturesAndAbsences"
import SignCode from "../../components/SignCode";
import SignQrCode from "../../components/SignQrCode";

// Components  
import Card from "../../components/Card";
import Dropdown from "../../components/Dropdown";
import Actions from "../../components/Actions";
import ModalCloseSessionFormation from "../../components/ModalCloseSession/ModalCloseSessionFormation";

// Services
import { getSessionFormationById, updateSessionFormation } from "../../services/sessions.service";
import { createAttendance, getAttendancesBySessionId, deleteAttendance, generateDocuments } from "../../services/attendances.service";
import { showSuccess, showError } from "../../services/toast.service";

const SessionsFormationDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [session, setSession] = useState(location.state?.session || null);
  const [loading, setLoading] = useState(!location.state?.session);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);

  // Onglets fixes à afficher
  const visibleTabs = ["Signatures & Absences", "Paramètres"];

  // Gestionnaire pour la clôture de session
  const handleOpenCloseModal = () => {
    setShowCloseModal(true);
  };

  const handleCloseModal = () => {
    setShowCloseModal(false);
  };

  const handleSessionClosed = async (sessionId) => {
    // Recharger les données de la session après clôture
    try {
      const updatedSession = await getSessionFormationById(sessionId);
      setSession(updatedSession);
    } catch (error) {
      console.error('Erreur lors du rechargement de la session:', error);
    }
    setShowCloseModal(false);
  };

  // Fonction pour synchroniser les attendances avec les participants
  const syncAttendancesWithParticipants = async (sessionId, oldParticipants, newParticipants, partnerId) => {
    try {
      // Récupérer les attendances existantes
      const existingAttendances = await getAttendancesBySessionId(sessionId);

      // Convertir en arrays si ce ne sont pas déjà des arrays
      const oldParticipantIds = Array.isArray(oldParticipants) ? oldParticipants : [];
      const newParticipantIds = Array.isArray(newParticipants) ? newParticipants : [];

      // Trouver les participants ajoutés (nouveaux participants)
      const addedParticipants = newParticipantIds.filter(id => !oldParticipantIds.includes(id));

      // Trouver les participants supprimés
      const removedParticipants = oldParticipantIds.filter(id => !newParticipantIds.includes(id));

      // Créer des attendances pour les nouveaux participants
      for (const participantId of addedParticipants) {
        try {
          const attendanceData = {
            session_id: sessionId,
            participant_id: participantId,
            partner_id: partnerId,
            status: 'Absent'
          };

          console.log('Création attendance pour nouveau participant:', participantId);
          await createAttendance(attendanceData);
        } catch (error) {
          console.error(`Erreur lors de la création de l'attendance pour le participant ${participantId}:`, error);
        }
      }

      // Supprimer les attendances des participants retirés
      for (const participantId of removedParticipants) {
        try {
          const attendanceToDelete = existingAttendances.find(att => att.participant_id === participantId);
          if (attendanceToDelete) {
            console.log('Suppression attendance pour participant retiré:', participantId);
            await deleteAttendance(attendanceToDelete.id);
          }
        } catch (error) {
          console.error(`Erreur lors de la suppression de l'attendance pour le participant ${participantId}:`, error);
        }
      }

    } catch (error) {
      console.error('Erreur lors de la synchronisation des attendances:', error);
    }
  };

  // Fonction pour télécharger la feuille d'émargement
  const handleDownloadAttendanceSheet = async () => {
    if (!session) return;

    try {
      setUpdating(true);

      // Appeler le service pour générer et télécharger la feuille d'émargement
      await generateDocuments(session.id);

      showSuccess('Feuille d\'émargement téléchargée avec succès!');
    } catch (error) {
      console.error('Erreur lors du téléchargement de la feuille d\'émargement:', error);
      showError('Erreur lors du téléchargement de la feuille d\'émargement. Veuillez réessayer.');
    } finally {
      setUpdating(false);
    }
  };

  // Fonction pour annuler la session
  const handleCancelSession = async () => {
    if (!session) return;

    // Demander confirmation avant d'annuler
    if (!window.confirm(`Êtes-vous sûr de vouloir annuler la session "${session.title}" ? Cette action est irréversible.`)) {
      return;
    }

    try {
      setUpdating(true);

      // Mettre à jour le statut de la session à "Annulé"
      await updateSessionFormation(session.id, { status: "Annulé" });

      // Recharger les données de la session
      const updatedSession = await getSessionFormationById(session.id);
      setSession(updatedSession);

      showSuccess('Session annulée avec succès!');
    } catch (error) {
      console.error('Erreur lors de l\'annulation de la session:', error);
      showError('Erreur lors de l\'annulation de la session. Veuillez réessayer.');
    } finally {
      setUpdating(false);
    }
  };

  // Fonction pour mettre à jour la session
  const handleUpdateSession = async (sessionData) => {
    if (!session) return;

    try {
      setUpdating(true);

      // Sauvegarder les anciens participants avant la mise à jour
      const oldParticipants = session.participants_id || [];

      await updateSessionFormation(session.id, sessionData);

      // Recharger les données de la session
      const updatedSession = await getSessionFormationById(session.id);

      // Synchroniser les attendances si les participants ont changé
      if (sessionData.participants_id) {
        await syncAttendancesWithParticipants(
          session.id,
          oldParticipants,
          sessionData.participants_id,
          session.partner_id
        );
      }

      setSession(updatedSession);

      showSuccess('Session de formation mise à jour avec succès!');
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la session:', error);
      showError(error.message || 'Erreur lors de la mise à jour de la session');
    } finally {
      setUpdating(false);
    }
  };

  // Récupérer la session depuis l'API si elle n'est pas déjà disponible
  useEffect(() => {
    // Si nous avons déjà la session depuis la navigation, ne pas la récupérer à nouveau
    if (location.state?.session) return;

    const fetchSession = async () => {
      setLoading(true);
      setError(null);
      try {
        const sessionData = await getSessionFormationById(id);
        if (sessionData) {
          setSession(sessionData);
        } else {
          setError("Session de formation non trouvée");
          setTimeout(() => navigate("/admin/sessions-formation/"), 2000);
        }
      } catch (err) {
        console.error("Erreur lors de la récupération de la session:", err);
        setError("Impossible de charger les détails de la session");
        setTimeout(() => navigate("/admin/sessions-formation/"), 2000);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchSession();
    } else {
      setError("ID de session non valide");
      setTimeout(() => navigate("/admin/sessions-formation/"), 2000);
    }
  }, [id, navigate, location.state]);

  // Ajuster l'activeIndex si nécessaire
  useEffect(() => {
    if (activeIndex >= visibleTabs.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, visibleTabs.length]);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loading}>Chargement des détails de la session...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.error}>{error}</div>
        <div>Redirection vers la liste des sessions...</div>
      </div>
    );
  }

  if (!session) {
    return null;
  }

  // Fonctions utilitaires pour formater les données SANS conversion timezone
  // La date est stockée telle quelle en BDD (ex: "2026-01-26 20:27:00")
  const formatDate = (dateString) => {
    if (!dateString) return 'Non définie';
    try {
      // Parser la date sans conversion timezone
      const [datePart] = dateString.split(' ');
      const [year, month, day] = datePart.split('-');
      return `${day}/${month}/${year}`;
    } catch (e) {
      return 'Date invalide';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Non définie';
    try {
      // Parser la date sans conversion timezone
      const [datePart, timePart] = dateString.split(' ');
      const [year, month, day] = datePart.split('-');
      const [hours, minutes] = timePart ? timePart.split(':') : ['00', '00'];
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (e) {
      return 'Date invalide';
    }
  };

  return (
    <>
      <div className={styles.shop}>
        <Link className={cn("button-white", styles.button)} to="/admin/sessions-formation">
          <Icon name="arrow-left" />
          Retour
        </Link>

        <div className={styles.background}>
          <img src="/images/content/bg-shop.jpg" alt="Background" />
        </div>

        <Card className={styles.card}>
          {/* Profile de la session */}
          <div className={styles.profile}>
            <div className={styles.details}>
              <div className={styles.avatar}>
                <img src={"/images/content/formation_logo.png"} alt={session.title} />
              </div>
              <div className={styles.wrap}>
                <div className={cn("h4", styles.man)}>{session.title}</div>
                <div className={styles.login}>
                  {session.expand?.teacher_id ?
                    `${session.expand.teacher_id.first_name} ${session.expand.teacher_id.last_name}` :
                    'Aucun intervenant assigné'
                  }
                </div>
              </div>
            </div>
            <div className={styles.statusContainer}>
              <div className={styles.statusGroup}>
                <div className={styles.login}>Statut</div>
                <div className={cn(
                  styles.statusBadge,
                  'status-type',
                  session.status === 'ouvert' ? 'status-green-dark' :
                    session.status === 'ferme' ? 'status-red-dark' :
                      'status-yellow-dark'
                )}>
                  {session.status || 'Non défini'}
                </div>
              </div>

              <div className={styles.statusGroup}>
                <div className={styles.login}>Date de début</div>
                <div className={cn(
                  styles.statusBadge,
                  'status-type',
                  'status-blue-dark'
                )}>
                  {formatDateTime(session.start_date)}
                </div>
              </div>

              <div className={styles.statusGroup}>
                <div className={styles.login}>Date de fin</div>
                <div className={cn(
                  styles.statusBadge,
                  'status-type',
                  'status-blue-dark'
                )}>
                  {formatDateTime(session.end_date)}
                </div>
              </div>
            </div>
          </div>


          <div className={styles.control}>
            <div className={cn(styles.nav, styles.desktopNav)}>
              {visibleTabs.map((x, index) => (
                <button
                  className={cn(styles.link, {
                    [styles.active]: index === activeIndex,
                  })}
                  onClick={() => setActiveIndex(index)}
                  key={index}
                >
                  {x}
                </button>
              ))}
            </div>

            <div className={styles.actionContainer}>
              {updating ? (
                <div className={styles.loading}>Mise à jour en cours...</div>
              ) : (
                <Actions
                  className={styles.actions}
                  classActionsHead={styles.actionsHead}
                  items={[
                    {
                      title: 'Clôturer la session',
                      action: handleOpenCloseModal
                    },
                    {
                      title: 'Télécharger',
                      action: handleDownloadAttendanceSheet
                    },
                    {
                      title: 'Annuler la session',
                      action: handleCancelSession
                    },
                  ]}
                  small
                />
              )}
            </div>

            <div className={styles.mobileNavContainer}>
              <div className={styles.mobileNav}>
                <Dropdown
                  className={styles.dropdown}
                  value={visibleTabs[activeIndex]}
                  setValue={(value) => {
                    const index = visibleTabs.findIndex(x => x === value);
                    if (index !== -1) setActiveIndex(index);
                  }}
                  options={visibleTabs}
                />
              </div>

              <div className={styles.mobileActions}>
                {updating ? (
                  <div className={styles.loading}>Mise à jour en cours...</div>
                ) : (
                  <Actions
                    className={styles.actions}
                    classActionsHead={styles.actionsHead}
                    items={[
                      {
                        title: 'Clôturer la session',
                        action: handleOpenCloseModal
                      },
                      {
                        title: 'Télécharger feuille d\'émargement',
                        action: handleDownloadAttendanceSheet
                      },
                      {
                        title: 'Annuler la session',
                        action: handleCancelSession
                      },
                    ]}
                    small
                  />
                )}
              </div>
            </div>
          </div>

        </Card>

        {/* Pages */}
        <div className={styles.section}>
          {visibleTabs[activeIndex] === "Signatures & Absences" && (
            <>
              {/* Deux cartes 50/50 */}
              <div className={styles.row}>
               <div className={styles.col}>
                  <SignCode 
                    className={styles.card} 
                    session={session}
                  />
                </div>
                <div className={styles.col}>
                   <SignQrCode 
                    className={styles.card} 
                    session={session}
                  />
                </div>
              </div>
              
              {/* Carte pleine largeur */}
              <SignaturesAndAbsences session={session} />
            </>
          )}
          {visibleTabs[activeIndex] === "Paramètres" && (
            <NameAndDescription
              session={session}
              onUpdateSession={handleUpdateSession}
              updating={updating}
            />
          )}
        </div>
      </div>

      {/* Modal de clôture de session */}
      <ModalCloseSessionFormation
        visible={showCloseModal}
        onClose={handleCloseModal}
        session={session}
        onSessionClosed={handleSessionClosed}
      />
    </>
  );
};

export default SessionsFormationDetail;
